// Client mirror of WellWorkspace::StagedWell's `on: :status_edit` validations, so the
// Edit Well Status panel can hold Save and name the broken rule before a round trip. The
// server re-validates on every PATCH and remains the source of truth — these messages are
// worded exactly as it words them, so an inline warning and a 422 read identically.
import {
  DATE_FIELD_LABELS,
  EDITABLE_DATE_FIELDS,
  REQUIRED_DATES_BY_STATUS,
  WELL_STATUS_ORDER,
  type StagedDateField,
  type StagedWell,
  type WellStatusToken,
} from 'types/well-workspace';

export interface StatusEditValues {
  status: WellStatusToken | null;
  permitDate: string | null;
  spudDate: string | null;
  rigReleaseDate: string | null;
  completionDate: string | null;
  // Operator-writable only on a well with no production (see canEditFirstProductionDate);
  // otherwise derived read-only. Carried on the form either way — the required-dates rule
  // reads it for `producing` whichever side supplied it.
  firstProductionDate: string | null;
  // Always derived read-only: it has no override column anywhere.
  lastProductionDate: string | null;
}

/**
 * What the server will accept for this well, mirroring StagedWell#editable_status_fields.
 * Omitted leaves first production date read-only, which is the safe default.
 */
export interface StatusEditContext {
  /** Mirror of `operator_first_prod_editable?` — see canEditFirstProductionDate. */
  firstProductionEditable: boolean;
  /** Canonical first production date, to tell whether the operator actually moved it. */
  baselineFirstProductionDate: string | null;
}

const READ_ONLY_FIRST_PRODUCTION: StatusEditContext = {
  firstProductionEditable: false,
  baselineFirstProductionDate: null,
};

/**
 * Whether an operator may supply this well's first production date.
 *
 * Mirrors `StagedWell#operator_first_prod_editable?`: editable precisely when the well has no
 * production to derive the date from — a well authored in the workspace (`return true if
 * new_well?`, no canonical twin yet) or an existing well whose production records are empty.
 * Either way the operator's value is the only source, and the server promotes it to
 * `fallback_first_production_date`.
 *
 * `lastProductionDate` stands in for "has production records": both production dates derive from
 * the same association and `well_production_records.date` is NOT NULL, so a null last date means
 * the collection is empty — which is also true of a brand-new well, so the one test covers both
 * halves of the server predicate. `firstProductionDate` would NOT do: once an override promotes it
 * reads back non-null while the field is still editable, which would re-lock it the moment the
 * operator set it.
 *
 * The staged row's date is a snapshot, so production landing mid-draft leaves this
 * offering a field the server will quietly ignore — it fails soft, and the saved response
 * shows the value unchanged.
 * @param {Pick<StagedWell, 'lastProductionDate'>} well The staged well.
 * @return {boolean} Whether the field should accept input.
 */
export const canEditFirstProductionDate = (
  well: Pick<StagedWell, 'lastProductionDate'>
): boolean => well.lastProductionDate === null;

/** One broken rule, tagged with the field it belongs to so the input can show it too. */
export interface StatusEditError {
  /** The form field the message should attach to. */
  field: 'status' | StagedDateField;
  message: string;
}

// The server humanizes a field as "<name without _date> date" — "first production date",
// not "First Production Date". Matching it keeps the two message sets identical.
const serverFieldName = (field: StagedDateField): string =>
  DATE_FIELD_LABELS[field].toLowerCase();

// Ditto for a status token: `shut_in` reads as "shut in", not "Shut In".
const serverStatusName = (status: string): string => status.replace(/_/g, ' ');

/**
 * Status presence and inclusion. A blank status clears nothing — it would promote a NULL
 * status onto the canonical well — so the server rejects it and so do we.
 * @param {StatusEditValues} values The form.
 * @return {StatusEditError[]} The broken rules.
 */
const statusInclusionErrors = (values: StatusEditValues): StatusEditError[] => {
  if (!values.status)
    return [{ field: 'status', message: 'Status is required' }];
  if (WELL_STATUS_ORDER.includes(values.status)) return [];

  return [
    {
      field: 'status',
      message: `${serverStatusName(
        values.status
      )} is not a recognized well status`,
    },
  ];
};

/**
 * Required dates by status (PRD Table 3), cumulative.
 * @param {StatusEditValues} values The form.
 * @return {StatusEditError[]} The broken rules.
 */
const requiredDateErrors = (values: StatusEditValues): StatusEditError[] => {
  if (!values.status) return [];

  return (REQUIRED_DATES_BY_STATUS[values.status] ?? [])
    .filter((field) => !values[field])
    .map((field) => ({
      field,
      message: `${serverFieldName(field)} is required for ${serverStatusName(
        values.status as string
      )} status`,
    }));
};

/**
 * Chronological ordering across the OPERATOR-EDITABLE dates, blanks skipped.
 *
 * The derived production dates are deliberately absent: the server orders only
 * `EDITABLE_STATUS_FIELDS - status`, because a vendor anomaly in a read-only date is not
 * something the operator can fix through this form and must not block an unrelated edit.
 * @param {StatusEditValues} values The form.
 * @param {StatusEditContext} context What the server will accept for this well.
 * @return {StatusEditError[]} The broken rules.
 */
const dateOrderingErrors = (
  values: StatusEditValues,
  context: StatusEditContext
): StatusEditError[] => {
  // The server appends first production date to the END of the chain, and only when the
  // operator actually MOVED it (`edited_fields.include?('first_production_date')`, which
  // is reachable only on a no-production well). Merely being present is not enough: a
  // pre-existing override that predates completion date must not block an unrelated edit,
  // which is the same reasoning that keeps the derived dates out of the chain entirely.
  const firstProductionMoved =
    context.firstProductionEditable &&
    values.firstProductionDate !== context.baselineFirstProductionDate;
  const chain: StagedDateField[] = firstProductionMoved
    ? [...EDITABLE_DATE_FIELDS, 'firstProductionDate']
    : [...EDITABLE_DATE_FIELDS];

  const present = chain.filter((field) => values[field]);

  return present.slice(0, -1).flatMap((earlier, index) => {
    const later = present[index + 1];
    // ISO dates sort lexicographically, which is why the strings compare directly.
    if ((values[earlier] as string) <= (values[later] as string)) return [];

    return [
      {
        field: earlier,
        message: `${serverFieldName(
          earlier
        )} must be on or before ${serverFieldName(later)}`,
      },
    ];
  });
};

/**
 * Every rule the server would apply to this edit, in the order it reports them.
 * @param {StatusEditValues} values The form.
 * @param {StatusEditContext} context What the server will accept for this well. Omitted
 *   leaves first production date read-only, which is the safe default.
 * @return {StatusEditError[]} The broken rules; empty means the edit is savable.
 */
export const validateStatusEdit = (
  values: StatusEditValues,
  context: StatusEditContext = READ_ONLY_FIRST_PRODUCTION
): StatusEditError[] => [
  ...statusInclusionErrors(values),
  ...requiredDateErrors(values),
  ...dateOrderingErrors(values, context),
];

/**
 * The rules a Story-3 manual add runs. The add row offers the FULL status list, every member of
 * which is legitimate for a brand-new well, so status inclusion is guaranteed and deliberately not
 * re-checked — only the DATE rules can break: required-dates-by-status and chronological order,
 * worded exactly as the server words them. A brand-new authored well has no production, so first
 * production date is operator-editable and participates in the ordering chain (mirroring
 * `operator_first_prod_editable?` returning true for a new well), with no canonical baseline to
 * compare against.
 * @param {StatusEditValues} values The manual row's form values.
 * @return {StatusEditError[]} The broken rules; empty means the row is addable.
 */
export const validateManualAddStatus = (
  values: StatusEditValues
): StatusEditError[] => [
  ...requiredDateErrors(values),
  ...dateOrderingErrors(values, {
    firstProductionEditable: true,
    baselineFirstProductionDate: null,
  }),
];

/**
 * The first message for one field, for an input's `invalidText`.
 * @param {StatusEditError[]} errors The broken rules.
 * @param {'status' | StagedDateField} field The field to look up.
 * @return {string | undefined} Its message, if it has one.
 */
export const errorFor = (
  errors: StatusEditError[],
  field: 'status' | StagedDateField
): string | undefined => errors.find((error) => error.field === field)?.message;
